import {
	colors,
	DynamicLink,
	Flex,
	Icon,
	READABLE_LINE_MAX_WIDTH,
	Switch,
	Text,
} from "@capioweb/ui-components/src"
import React from "react"
import { useSearchParams } from "react-router-dom"
import styled from "styled-components"
import { PaginationContext } from "../../../components/PaginationBlock/context/PaginationContext"
import { isSessionStorageAvailable, sendInternalSearchDataLayerPush } from "../../../utils"
import { SearchResultItem } from "../@types/SearchResultItem"
import { SiteSearchFilterContext } from "../context"
import { AppContext, LocalesContext } from '../../../context'

interface SiteSearchListingProps {
	items: SearchResultItem[]
}
interface SearchListingCardProps {
	item: SearchResultItem
	bgColor: boolean
}

interface StyledSearchListingCardProps {
	bgColor: boolean
}

const StyledSearchListCard = styled.div<StyledSearchListingCardProps>`
	width: 100%;
	padding: 24px;
	flex-direction: column;
	background-color: ${(props) => (props.bgColor ? colors.purple11 : "white")};
	border-radius: 8px;
	border-width: 1px;
	border-style: solid;
	border-color: transparent;

	&:hover {
		border-color: ${colors.hoverBlue};

		svg,
		path {
			fill: ${colors.hoverBlue};
		}
	}
`
const SearchListingCard: React.FC<SearchListingCardProps> = ({
	item,
	bgColor,
}) => {
	return (
		<StyledSearchListCard bgColor={bgColor}>
			{/* Row one */}
			<Flex w={1} justifyContent="space-between">
				<Text as="h3" variant="headline-3" mb={1}>
					{item.title}
				</Text>
				<Flex>
					<Icon icon="arrow-right" />
				</Flex>
			</Flex>
			{/* Row two */}
			<Flex
				columnGap={7.5}
				pb={1}
				mb={2}
				borderBottom
				borderWidth="2px"
				style={
					bgColor
						? { borderColor: colors.purple33 }
						: { borderColor: colors.blue11 }
				}
			>
				<Flex columnGap={0.5}>
					<Flex>
						<Icon icon={item.icon} />
					</Flex>
					<Text mb={0}>{item.typeName}</Text>
				</Flex>
				{item.location && (
					<Flex>
						<Flex>
							<Icon icon="pin" />
						</Flex>
						<Text mb={0}>{item.location}</Text>
					</Flex>
				)}
			</Flex>
			{/* Row three */}
			<Text>{item.excerpt}</Text>
		</StyledSearchListCard>
	)
}

const SiteSearchListing: React.FC<SiteSearchListingProps> = ({ items }) => {
	const { activeItems } = React.useContext(SiteSearchFilterContext)
	const { activePage } = React.useContext(PaginationContext)
	const { translate, translationKeys } = React.useContext(LocalesContext)
	const { userLocation, getUserLocation } = React.useContext(AppContext)
	const [allowGeoLocation, setAllowGeoLocation] = React.useState<boolean>(false)

	const [page, setPage] = React.useState<number>(1)
	const [searchParams] = useSearchParams()

	const handleGeoPositionSwitch = () => {

		getUserLocation()
		if (isSessionStorageAvailable()) {
			if (sessionStorage.getItem("showMyPos") === "1") {
				sessionStorage.removeItem("showMyPos")
			} else {
				sessionStorage.setItem("showMyPos", "1")
			}
		}
	}


	function checkUserLocationPermission() {
		navigator.permissions.query({ name: 'geolocation' }).then((result) => {
			if (result.state === 'granted') {
				navigator.geolocation.getCurrentPosition(
					(position) => {
						setAllowGeoLocation(true)
					},
					(error) => {
						console.error('Error getting location:', error)
						setAllowGeoLocation(false)
					}
				)
			} else if (result.state === 'prompt') {
				navigator.geolocation.getCurrentPosition(
					(position) => {
						setAllowGeoLocation(true)
					},
					(error) => {
						console.error('Error getting location:', error)
						setAllowGeoLocation(false)
					}
				)
			} else if (result.state === 'denied') {
				console.log('User has denied location access')
				setAllowGeoLocation(false)
			}
		})
	}


	React.useEffect(() => {
		setPage(Number(activePage))
	}, [activePage])

	React.useEffect(() => {
		sendInternalSearchDataLayerPush(searchParams.get("query"), items.length)
	}, [items])

	React.useEffect(() => {
		if (isSessionStorageAvailable()) {
			if (sessionStorage.getItem("showMyPos") === "1" && !userLocation) {
				getUserLocation()
			}
		}
	}, [])


	React.useEffect(() => {
		checkUserLocationPermission()
	}, [userLocation])

	return (
		<>
			<Flex
				flexDirection="column"
				maxw={READABLE_LINE_MAX_WIDTH}
				w={1}
				m="auto"
				mb={4}
			>
				{allowGeoLocation &&

					<Flex mb={{ _: 8, m: 2 }} mx='auto' mr={{ m: 0 }}>
						<Switch
							onChange={(e) => handleGeoPositionSwitch()}
							label={translate(translationKeys.useMyPosition)}
							defaultChecked={sessionStorage.getItem("showMyPos") === "1"}
						/>
					</Flex>
				}
				{activeItems
					.slice(page * 10 - 10, page * 10)
					.map((item: SearchResultItem, i) => {
						return (
							<DynamicLink
								url={item.url}
								key={i}
								target={item.typeId === "careers" ? "blank" : ''}
							>
								<SearchListingCard item={item} bgColor={!(i % 2 === 0)} />
							</DynamicLink>
						)
					})}
			</Flex>
		</>
	)
}

export { SiteSearchListing }
